/**
 * \u5165\u53e3 \u2014\u2014 \u7ec4\u88c5 Game + AnimationDirector + BoardRenderer + InputController + HUD\u3002
 *
 * \u5faa\u73af\uff1a\u6301\u7eed rAF\uff08\u5e26 delta\uff09\u3002
 * \u4e00\u6b65\u6d41\u7a0b\uff1a
 *   \u8f93\u5165\u610f\u56fe \u2192 Game.trySwap\uff08\u540c\u6b65\u7b97\u5b8c\u5e76\u8bb0\u5f55 MoveRecord\uff09
 *   \u2192 AnimationDirector.play\uff08\u6309\u65f6\u95f4\u8f74\u64ad\u653e\uff09\u2192 Game \u89e3\u9501
 */

import { Game } from './core/game';
import { BoardRenderer, computeMetrics } from './render/board-renderer';
import { AnimationDirector } from './anim/director';
import { InputController } from './input/controller';
import { grabHud, renderHud, showToast, showResultModal, hideResultModal } from './ui/hud';
import { initPanels } from './ui/panels';
import type { Board0, Coord, PieceColor } from './core/types';
import { BOARD_SIZE } from './core/types';
import { getGemSpec, applyColorblindMode, type ColorKey } from './render/palette';
import { clearSpriteCache, warmupSprites } from './render/gem';
import { setReducedMotion } from './anim/motion';
import { getLevelCount } from './core/level';
import { SaveStore } from './core/save';
import { SettingsStore, type ColorblindMode } from './core/settings';
import { CodexStore } from './core/codex';

const canvas = document.getElementById('board') as HTMLCanvasElement;
const stage = document.getElementById('stage') as HTMLElement;
const hud = grabHud();

let currentLevel = 1;
let game: Game;
let renderer: BoardRenderer;
let input: InputController;
let director: AnimationDirector;

/** \u8f93\u5165\u9501\uff1a\u52a8\u753b\u64ad\u653e\u671f\u95f4\u7981\u6b62\u64cd\u4f5c */
let inputLocked = false;
let lastFrameTime = 0;
let rafId = 0;

/** S9 存档单例：模块加载即读档（首帧前），隐私模式下内存降级、刷新丢失 */
const save = SaveStore.instance;
const settings = SettingsStore.instance;
const codex = CodexStore.instance;

function layout(): { width: number; height: number } {
  const rect = stage.getBoundingClientRect();
  return { width: Math.max(240, rect.width), height: Math.max(240, rect.height) };
}

function boot(levelId: number): void {
  currentLevel = levelId;
  game = new Game(levelId);
  // S10：构造函数已 emit LevelStarted，但 bindGameEvents 在其后才订阅；这里补记本关目标色（解锁判定用）
  codex.handleLevelStarted({ goals: game.snapshot().level.goals });
  inputLocked = false;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const { width, height } = layout();
  const metrics = computeMetrics(width, height, dpr);

  if (!renderer) {
    renderer = new BoardRenderer(canvas, metrics);
  } else {
    renderer.applySize(metrics);
  }

  // \u52a8\u753b\u5bfc\u6f14\uff08\u53ea\u521d\u59cb\u5316\u4e00\u6b21\uff0c\u4f9d\u8d56\u6e32\u67d3\u5668\u7684\u5750\u6807\u6362\u7b97\uff09
  if (!director) {
    director = new AnimationDirector({
      cellCenterXY: (c, r) => renderer.cellCenter({ c, r }),
    colorOf: (key) => {
      const spec = getGemSpec(key as ColorKey);
      return spec ? spec.mid : '#8E97A6';
    },
    });
  } else {
    director.reset();
  }

  if (input) input.dispose();
  input = new InputController(canvas, {
    ox: metrics.ox,
    oy: metrics.oy,
    cellPitch: metrics.cellPitch,
  }, {
    onSwapIntent: (from: Coord, to: Coord) => {
      if (inputLocked || game.isBusy() || game.snapshot().finished !== 'playing') return;
      // 交换前快照：视觉棋盘回放的起点（逻辑层即将同步算完终局）
      const preBoard: Board0 = {
        size: BOARD_SIZE,
        cells: game.snapshot().board.cells.map((row) => row.map((c) => ({ ...c }))),
      };
      const ok = game.trySwap(from, to);
      if (!ok) {
        showToast(hud, '\u8fd9\u4e24\u9897\u6362\u4e0d\u4e86');
        markDirty();
        return;
      }
      startMoveAnimation(preBoard);
    },
    onSelectionChanged: (state, coord) => {
      renderer.setSelection(state === 'SELECTED' && coord ? coord : null);
      markDirty();
    },
    onDragVisual: (v) => {
      renderer.setDrag(v.active ? v.from : null, v.dx, v.dy, v.active ? v.over : null);
      markDirty();
    },
    onHover: (coord) => {
      renderer.setHover(coord);
      markDirty();
    },
  });

  bindGameEvents();
  hideResultModal(hud);
  renderHud(hud, game.snapshot());
  markDirty();
  startLoop();
}

/** \u542f\u52a8\u4e00\u6b65\u7684\u52a8\u753b\u64ad\u653e\uff08\u63a5\u6536\u4ea4\u6362\u524d\u5feb\u7167\u4f5c\u4e3a\u89c6\u89c9\u68cb\u76d8\u8d77\u70b9\uff09 */
function startMoveAnimation(preBoard: Board0): void {
  const record = game.takeLastMove();
  if (!record) return;

  inputLocked = true;
  input.clearSelection();
  renderer.setSelection(null);
  renderer.setDrag(null, 0, 0, null);

  director.play(record, {
    preBoard,
    finalBoard: game.snapshot().board,
    onFinished: () => {
      inputLocked = false;
      renderHud(hud, game.snapshot());
      markDirty();
      // 结算弹窗延后到动画播完
      const snap = game.snapshot();
      if (snap.finished !== 'playing') {
        window.setTimeout(() => {
          if (game.snapshot().finished !== 'playing') showResultModal(hud, game.snapshot());
        }, 220);
      }
    },
    onFrame: markDirty,
  });
}

function bindGameEvents(): void {
  // S10：局中被动记账目标色（重建 Game 时构造已 emit，bus 订阅覆盖 restart 的重发）
  game.bus.on('LevelStarted', (e: { goals?: { type: string; color?: PieceColor }[] }) => {
    codex.handleLevelStarted(e);
  });
  game.bus.on('LevelComplete', (e: { levelId: number; stars: number; score: number; movesLeft: number }) => {
    renderHud(hud, game.snapshot());
    // S9：通关写入（星级 / 最高分单调不减）
    save.recordLevelResult({ type: 'LevelComplete', levelId: e.levelId, stars: e.stars, score: e.score });
    // S10：通关解锁本关目标色星球
    codex.handleLevelComplete(e);
  });
  game.bus.on('LevelFailed', (e: { levelId: number; score: number }) => {
    renderHud(hud, game.snapshot());
    // S9：失败仅更新最高分
    save.recordLevelResult({ type: 'LevelFailed', levelId: e.levelId, score: e.score });
  });
}

// ── 主循环 ──

/**
 * 脏帧标志：只有画面确实需要更新时才重绘。
 * 静止形态（无动画、无拖拽、无选中变化）下跳过 render，
 * 避免无意义的 GPU 提交与合成器抖动。
 */
let needsRedraw = true;

/** 标记本帧需要重绘（由输入与动画回调调用） */
function markDirty(): void { needsRedraw = true; }

function startLoop(): void {
  cancelAnimationFrame(rafId);
  lastFrameTime = performance.now();
  const frame = (now: number) => {
    const dt = Math.min(100, now - lastFrameTime);
    lastFrameTime = now;

    const wasPlaying = director.isPlaying;
    if (wasPlaying) {
      director.tick(dt);
      // 动画播完的那一刻必须再画一帧落定画面
      if (!director.isPlaying) needsRedraw = true;
    }

    if (needsRedraw) {
      renderer.render(game.snapshot(), director.state);
      needsRedraw = false;
    }

    rafId = requestAnimationFrame(frame);
  };
  rafId = requestAnimationFrame(frame);
}

function handleResize(): void {
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const { width, height } = layout();
  const metrics = computeMetrics(width, height, dpr);
  renderer.applySize(metrics);
  if (input) {
    input.setLayout({ ox: metrics.ox, oy: metrics.oy, cellPitch: metrics.cellPitch });
  }
  markDirty();
}

// \u2500\u2500 \u4ea4\u4e92\u6309\u94ae \u2500\u2500

function resetToLevel(levelId: number): void {
  hideResultModal(hud);
  input.clearSelection();
  renderer.setSelection(null);
  renderer.setDrag(null, 0, 0, null);
  director.reset();
  inputLocked = false;
  currentLevel = levelId;
  game.restart();
  renderHud(hud, game.snapshot());
  markDirty();
}

hud.btnRestart.addEventListener('click', () => { resetToLevel(currentLevel); });

hud.modalRetry.addEventListener('click', () => { resetToLevel(currentLevel); });

hud.modalNext.addEventListener('click', () => {
  hideResultModal(hud);
  const next = currentLevel + 1;
  if (next > getLevelCount()) {
    showToast(hud, '\u5207\u7247\u5230\u6b64\u4e3a\u6b62 \u2014\u2014 \u540e\u7eed\u5173\u5361\u5f85 P4 \u5f00\u653e', 2200);
    resetToLevel(currentLevel);
    return;
  }
  boot(next);
});

window.addEventListener('resize', handleResize);
window.addEventListener('orientationchange', () => {
  window.setTimeout(handleResize, 120);
});

// ── S10 / S12 元系统接线（唯一接线层） ──

/** S10：图鉴事件 → S9 存档指针 + hasNew */
codex.bus.on('CodexUnlocked', (e: { planetId: string }) => { save.addCodexUnlock(e.planetId); });
codex.bus.on('CodexViewed', () => { save.markCodexViewed(); });

/** S12：设置变更 → 运行时即时应用（E5 色盲局中重烘焙 / SET-01 减少动效） */
settings.subscribe((e) => {
  if (e.key === 'colorblindMode') {
    applyColorblindMode(e.value as ColorblindMode);
    clearSpriteCache();
    if (renderer) warmupSprites(renderer.metrics.cellSize, renderer.metrics.dpr);
    markDirty();
  } else if (e.key === 'reducedMotion') {
    setReducedMotion(settings.getResolved('reducedMotion'));
    markDirty();
  }
});

/** 首帧前应用设置（S12 §2.6 / §2.7）：先读设置 → 应用 → 再渲染，避免错误档位闪帧 */
function applyBootstrapSettings(): void {
  // E6：首次启动且系统偏好减少动效 → 记入 userPref（不覆盖已存档）
  if (!save.hadSavedData && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    settings.set('reducedMotion', true);
  }
  applyColorblindMode(settings.get('colorblindMode'));
  setReducedMotion(settings.getResolved('reducedMotion'));
}

initPanels({ settings, codex, save, hud });
applyBootstrapSettings();
boot(1);
